import {
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Space,
} from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import CustomButton from 'components/custom-button';
import CustomizedUpload from 'components/customized-upload';
import { REGEX_USERNAME, UPLOAD_TYPE } from 'constants/common';
import { REQUIRED } from 'constants/rule-form';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useCallback, useEffect, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { CHECK_FILE } from 'utils/rule-form';
import DebounceSelect from '../../../components/debounce-select/select';
import styles from '../styles.module.scss';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { SERVER_FILE_ENDPOINT } from '../../../constants/envs';

const { Option } = Select;
dayjs.extend(utc);
dayjs.extend(timezone);
const VN = 'Asia/Ho_Chi_Minh';
const supportIso = (date: any) => dayjs.tz(dayjs(date).format('YYYY-MM-DD HH:mm'), 'YYYY-MM-DD HH:mm', VN).toISOString();
interface DataPost {
  startDate: string;
  link?: string;
  name: string;
  image: any;
  user: any;
  supportEnabled?: boolean;
  supportEndAt?: any;
  supportLocation?: string;
}

interface Props {
  isOpen: boolean;
  handleCancelCreateEditModal: () => void;
  onChangeStatusUpdateData: () => void;
  valueUserSelected: any;
}

// eslint-disable-next-line arrow-body-style
// Can not select days before today and today
const disabledDate: RangePickerProps['disabledDate'] = (current) => {
  return current && current.add(1, 'day') < dayjs().endOf('day');
};

const disabledDateTime = () => ({
});

const ModalCreateLiveShow = ({
  isOpen,
  valueUserSelected,
  handleCancelCreateEditModal,
  onChangeStatusUpdateData,
}: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [listGroups, setListGroup] = useState<any>([]);
  const [checkIdol, setCheckIdol] = useState(false);
  const [supportMeta, setSupportMeta] = useState<any>(null);
  const [supportSchedule, setSupportSchedule] = useState<any>(null);
  const [createdShowId, setCreatedShowId] = useState<number | null>(null);
  const selectedCreator = Form.useWatch('user', form);
  const npcId = Number(selectedCreator?.value || selectedCreator?.key);
  const isNpcSelected = !checkIdol && supportMeta?.npcs?.some((n: any) => n.id === npcId);

  useEffect(() => {
    if (!isOpen) return;
    Promise.all([
      AxiosUtils.get(API.liveSupportAdmin + '/meta'),
      AxiosUtils.get(API.liveSupportAdmin + '/sessions'),
    ]).then(([meta, sessions]: any[]) => {
      setSupportMeta(meta.data);
      const linked = sessions.data.find((s: any) => s.liveShowId === valueUserSelected?.id);
      setSupportSchedule(linked || null);
      form.setFieldsValue({
        supportEnabled: !!linked && linked.status === 'SCHEDULED',
        supportEndAt: linked ? dayjs(linked.endAt).tz(VN) : undefined,
        supportLocation: linked?.location || '',
      });
    }).catch(() => setSupportMeta(null));
  }, [isOpen, valueUserSelected?.id, form]);

  useEffect(() => {
    const fetchGroupList = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.groupShowList);
        setListGroup(res?.data);
      } finally {
        setLoading(false);
      }
    };

    fetchGroupList();
  }, []);

  useEffect(() => {
    if (valueUserSelected) {
      const idolId: any = {};
      if (valueUserSelected.tiktokIdol) {
        setCheckIdol(true);
      }

      const groupId: any = {};

      if (valueUserSelected?.user) {
        idolId.key = valueUserSelected?.user?.id;
        idolId.value = valueUserSelected?.user?.id;
        idolId.label = valueUserSelected?.user?.username;
      }

      if (valueUserSelected.group) {
        groupId.key = valueUserSelected?.group?.id;
        groupId.label = valueUserSelected?.group.name;
        groupId.value = valueUserSelected?.group.id;
      }

      form.setFieldsValue({
        startDate: dayjs(valueUserSelected?.startDate),
        user: idolId,
        tiktokIdol: valueUserSelected?.tiktokIdol || '',
        link: valueUserSelected?.link || '',
        groupId: groupId,
        image: valueUserSelected?.image
          ? {
              fileKey: valueUserSelected?.image,
              fileUrl: `${SERVER_FILE_ENDPOINT}/${valueUserSelected.image}`,
            }
          : null,
      });
    }
  }, [form, valueUserSelected]);

  const filterOptions = useCallback(async (search: string) => {
    const response: any = await AxiosUtils.get(`${API.searchUser}?query=${search}`);
    return response.data.map((item: any) => ({
      id: item.id,
      label: item.username,
      value: item.id,
    }));
  }, []);

  const onFinish = async (values: DataPost) => {
    const { supportEnabled, supportEndAt, supportLocation, ...publicValues } = values;
    const newValues = { ...publicValues };

    newValues.image = values?.image?.fileKey;

    newValues.startDate = dayjs(values.startDate).format();

    if (supportEnabled) {
      if (!isNpcSelected || !supportMeta?.routes?.some((r: any) => r.npcUserId === npcId)) {
        notification.warning({ message: 'Cần ghép KTV cho NPC trước', description: 'Vào Liveshow → Gọi KTV cho NPC → Ghép NPC - KTV.' });
        return;
      }
      if (!supportEndAt || !supportLocation?.trim() ||
        dayjs(supportEndAt).valueOf() <= dayjs(values.startDate).valueOf()) {
        notification.warning({ message: 'Cần giờ kết thúc và vị trí LIVE hợp lệ' });
        return;
      }
    }

    const saveSupport = async (showId: number) => {
      if (supportEnabled) {
        const payload = {
          npcUserId: npcId,
          location: supportLocation?.trim() || '',
          startAt: supportIso(values.startDate),
          endAt: supportIso(supportEndAt),
          liveShowId: showId,
        };
        if (supportSchedule?.id) await AxiosUtils.patch(`${API.liveSupportAdmin}/sessions/${supportSchedule.id}`, payload);
        else await AxiosUtils.post(`${API.liveSupportAdmin}/sessions`, payload);
      } else if (supportSchedule?.id && supportSchedule.status === 'SCHEDULED') {
        await AxiosUtils.post(`${API.liveSupportAdmin}/sessions/${supportSchedule.id}/cancel`, {});
      }
    };

    setLoading(true);

    if (valueUserSelected?.id || createdShowId) {
      try {
        const showId = valueUserSelected?.id || createdShowId;
        await AxiosUtils.patch(`${API.createShow}/${showId}`, {
          ...newValues,
          userId: values?.user?.value || null,
        });
        await saveSupport(showId);
        handleCancelCreateEditModal();
        onChangeStatusUpdateData();
        notification.success({
          message: 'Thành công',
          description: supportEnabled ? 'Đã lưu phòng live và lịch gọi KTV.' : 'Đã lưu phòng live.',
          duration: 4,
        });
      } catch (error) {
        notification.error({
          message: 'Không thành công',
          description: 'Không lưu được lịch hỗ trợ. Phòng live có thể đã lưu; hãy bấm Lưu lại, không thêm phòng mới.',
          duration: 4,
        });
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const show: any = await AxiosUtils.post(API.createShow, {
          ...newValues,
          userId: values?.user?.value || null,
        });
        const showId = Number(show?.data?.id);
        if (showId) setCreatedShowId(showId);
        if (supportEnabled && !showId) throw new Error('Không lấy được ID phòng live để gắn lịch KTV');
        if (showId) await saveSupport(showId);
        setCreatedShowId(null);
        handleCancelCreateEditModal();
        onChangeStatusUpdateData();
        notification.success({
          message: 'Thành công',
          description: supportEnabled ? 'Đã tạo phòng live và lịch gọi KTV.' : 'Đã tạo phòng live.',
          duration: 4,
        });
      } catch (error) {
        notification.error({
          message: 'Không thành công',
          description: 'Phòng live có thể đã tạo nhưng lịch hỗ trợ chưa lưu. Bấm Lưu lại để hoàn tất, không tạo lại phòng.',
          duration: 4,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <Modal
        forceRender
        open={isOpen}
        title={valueUserSelected?.id ? 'Sửa phòng live' : 'Thêm phòng live'}
        onCancel={handleCancelCreateEditModal}
        width={800}
        getContainer={false}
        footer={false}
      >
        <Form
          name="form"
          form={form}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          onFinish={onFinish}
          autoComplete="off"
        >
          {/*<label className={styles.labelInput} htmlFor="name">*/}
          {/*  Tên Live Show*/}
          {/*</label>*/}
          {/*<Form.Item*/}
          {/*  name="name"*/}
          {/*  rules={[*/}
          {/*    REQUIRED,*/}
          {/*    {*/}
          {/*      max: 200,*/}
          {/*      message: 'Không được quá 200 kí tự',*/}
          {/*    },*/}
          {/*  ]}*/}
          {/*  wrapperCol={{ span: 24 }}*/}
          {/*>*/}
          {/*  <Input placeholder="Nhập tên Live Show" />*/}
          {/*</Form.Item>*/}

          <label className={styles.labelInput} htmlFor="groupId">
            Nhóm liveshow
          </label>
          <Form.Item rules={[REQUIRED]} name="groupId" wrapperCol={{ span: 24 }}>
            <Select placeholder="Chọn nhóm liveshow" size="large">
              {listGroups.map((item: any) => (
                <Option key={item?.id} value={item?.id}>
                  {item?.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <label className={styles.labelInput} htmlFor="name">
            Liên kết phòng live
          </label>
          <Form.Item name="link" wrapperCol={{ span: 24 }}>
            <Input size={'large'} placeholder="Nhập link phòng live" />
          </Form.Item>

          <label htmlFor="">Creator (TikTok ID)</label>
          <Row gutter={[16, 0]} className="d-flex justify-center">
            <Col span={15}>
              {checkIdol ? (
                <Form.Item
                  name="tiktokIdol"
                  rules={[
                    REQUIRED,
                    {
                      max: 100,
                      message: 'Không được quá 100 kí tự',
                    },
                    {
                      pattern: new RegExp(REGEX_USERNAME),
                      message: 'Nhập đúng format user',
                    },
                  ]}
                  wrapperCol={{ span: 24 }}
                  validateFirst={true}
                >
                  <Input placeholder="Nhập TikTok ID của Creator" size="large" />
                </Form.Item>
              ) : (
                <Form.Item name="user" wrapperCol={{ span: 24 }} rules={[REQUIRED]}>
                  <DebounceSelect placeholder="Chọn Creator" fetchOptions={filterOptions} />
                </Form.Item>
              )}
            </Col>
            <Col span={9} className={styles.checkboxCell}>
              <Checkbox
                checked={checkIdol}
                onChange={(e: CheckboxChangeEvent) => {
                  e.target.checked
                    ? form.setFieldsValue({
                        user: undefined,
                      })
                    : form.setFieldsValue({
                        tiktokIdol: '',
                      });
                  setCheckIdol(e.target.checked);
                }}
              >
                Không tìm thấy Creator
              </Checkbox>
            </Col>
          </Row>
          {/*<Col span={24}>*/}
          {/*  <Form.Item name="user" wrapperCol={{ span: 24 }} rules={[REQUIRED]}>*/}
          {/*    <DebounceSelect placeholder="Chọn Idol PK1" fetchOptions={filterOptions} />*/}
          {/*  </Form.Item>*/}
          {/*</Col>*/}

          <label className={styles.labelInput} htmlFor="startDate">
            Thời gian bắt đầu
          </label>
          <Form.Item rules={[REQUIRED]} name="startDate" wrapperCol={{ span: 24 }}>
            <DatePicker
              size="large"
              className={styles.fullWidth}
              format="YYYY-MM-DD HH:mm"
              disabledDate={disabledDate}
              disabledTime={disabledDateTime}
              showTime={{ defaultValue: dayjs('00:00:00', 'HH:mm:ss') }}
            />
          </Form.Item>

          {isNpcSelected && (
            <>
              <Form.Item name="supportEnabled" valuePropName="checked" wrapperCol={{ span: 24 }}>
                <Checkbox>Cho NPC gọi KTV trong giờ LIVE này</Checkbox>
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.supportEnabled !== cur.supportEnabled}>
                {({ getFieldValue }) => getFieldValue('supportEnabled') ? (
                  <>
                    <label htmlFor="supportEndAt">Giờ kết thúc LIVE thực tế · giờ VN (lịch công khai chỉ có 15 phút)</label>
                    <Form.Item name="supportEndAt" rules={[REQUIRED]} wrapperCol={{ span: 24 }}>
                      <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
                    </Form.Item>
                    <label htmlFor="supportLocation">Phòng / vị trí KTV cần đến</label>
                    <Form.Item name="supportLocation" rules={[REQUIRED]} wrapperCol={{ span: 24 }}>
                      <Input maxLength={120} placeholder="Ví dụ: Studio 3 · tầng 2" />
                    </Form.Item>
                  </>
                ) : null}
              </Form.Item>
            </>
          )}

          <label className={styles.labelInput} htmlFor="image">
            Ảnh
          </label>
          <Form.Item name="image" wrapperCol={{ span: 24 }} rules={[CHECK_FILE]}>
            <CustomizedUpload type={UPLOAD_TYPE.IMAGE} maxFileSize={20} />
          </Form.Item>

          <div className="d-flex justify-center">
            <Space size="large">
              <CustomButton
                bgColor="secondary-green"
                text="Lưu"
                htmlType="submit"
                loading={loading}
              />
              <CustomButton
                text="Hủy"
                bgColor="primary-grey"
                htmlType="button"
                onClick={handleCancelCreateEditModal}
              />
            </Space>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default ModalCreateLiveShow;
